import { useEffect, useRef, useState } from 'react';

import { NotificationKind, type Notification } from '../../api/generated/model';

/**
 * Fluxo de avisos em tempo real, escrito a mao — e nao gerado.
 *
 * `streamNotifications` carrega `x-client-generation: manual` no contrato e por
 * isso fica **fora** do recorte de onde o cliente TypeScript nasce. O motivo e
 * concreto: a resposta e `text/event-stream`, e um cliente HTTP comum espera o
 * corpo inteiro antes de resolver. Num fluxo que so termina quando o servidor
 * fecha a conexao, isso significa uma promessa pendente e uma conexao presa por
 * montagem de tela. No navegador, SSE e `EventSource`.
 *
 * `EventSource` e, junto com o link do `.ics`, a unica excecao prevista pelo
 * contrato ao "toda chamada vem do cliente gerado": nao ha `fetch` escrito aqui,
 * e o invariante `no-manual-client-operation.test.ts` prova que o gerador
 * continua sem produzir hook para esta operacao.
 *
 * **Queda nao quebra a tela, degrada.** Ao erro, a conexao e fechada, o estado
 * vira `polling` — e quem consome volta a consultar por intervalo — e uma
 * reconexao e agendada com recuo exponencial. Se ela abrir, o estado volta a
 * `streaming`.
 */

/** Rota do fluxo. Relativa, como todo o resto: o cookie de sessao e same-origin. */
export const NOTIFICATION_STREAM_URL = '/v1/notifications/stream';

/** Intervalo da consulta de reserva enquanto o fluxo esta caido. */
export const POLL_INTERVAL_MS = 15_000;

const FIRST_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

export type StreamStatus = 'connecting' | 'streaming' | 'polling';

export function useNotificationStream(onNotification: (notification: Notification) => void): StreamStatus {
  // Ambiente sem `EventSource` ja nasce em consulta por intervalo: e o unico
  // caminho que existe ali, e descobrir isso dentro do efeito custaria um
  // render a mais para chegar a mesma conclusao.
  const [status, setStatus] = useState<StreamStatus>(() =>
    typeof EventSource === 'undefined' ? 'polling' : 'connecting',
  );
  const handler = useRef(onNotification);

  useEffect(() => {
    handler.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    if (typeof EventSource === 'undefined') return;

    let source: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let stopped = false;

    const receive = (event: Event): void => {
      const data = (event as MessageEvent<string>).data;
      if (typeof data !== 'string') return;
      try {
        handler.current(JSON.parse(data) as Notification);
      } catch {
        // Evento ilegivel nao derruba a tela: o proximo pode vir bom, e a
        // consulta por intervalo continua sendo a rede de seguranca.
      }
    };

    const connect = (): void => {
      if (stopped) return;

      const opened = new EventSource(NOTIFICATION_STREAM_URL, { withCredentials: true });
      source = opened;

      opened.onopen = () => {
        attempt = 0;
        setStatus('streaming');
      };

      // Cada evento vem nomeado pelo `NotificationKind`, e nao no evento
      // `message` default — sem escutar por nome, nada chegaria.
      for (const kind of Object.values(NotificationKind)) {
        opened.addEventListener(kind, receive);
      }
      opened.onmessage = receive;

      opened.onerror = () => {
        opened.close();
        setStatus('polling');
        const delay = Math.min(FIRST_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
        attempt += 1;
        timer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      stopped = true;
      if (timer !== undefined) clearTimeout(timer);
      source?.close();
    };
  }, []);

  return status;
}
